class BaseError extends Error {
    statusCode;
    key;
    constructor(message, options) {
        super(message, options);
    }
    withKey(key) {
        this.key = key;
        return this;
    }
}
class InternalServerError extends BaseError {
    message;
    #tag = "InternalServerError";
    name = "InternalServerError";
    statusCode = 500;
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class BadRequestError extends BaseError {
    message;
    #tag = "BadRequestError";
    name = "BadRequestError";
    statusCode = 400;
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class ConflictError extends BaseError {
    message;
    #tag = "ConflictError";
    name = "ConflictError";
    statusCode = 409;
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class BondingLinePlacementError extends BaseError {
    #tag = "BondingLinePlacementError";
    name = "BondingLinePlacementError";
    statusCode = 409;
    key = "bonding_line_placement";
    constructor(options) {
        super("The bond request could not be placed on a bonding line", options);
    }
}
class StaleVersionError extends BaseError {
    entityName;
    entityId;
    #tag = "StaleVersionError";
    name = "StaleVersionError";
    statusCode = 409;
    constructor(entityName, entityId, options) {
        super(`${entityName} ${entityId} has been updated since it was last fetched`, options);
        this.entityName = entityName;
        this.entityId = entityId;
    }
}
class DatabaseError extends BaseError {
    #tag = "DatabaseError";
    name = "DatabaseError";
    statusCode = 500;
    constructor(message, options) {
        super(message, options);
    }
}
class EntityNotFoundError extends BaseError {
    entityName;
    entityId;
    #tag = "EntityNotFoundError";
    name = "EntityNotFoundError";
    statusCode = 404;
    constructor(entityName, entityId, appendedMessage, options) {
        super(`${entityName} ${entityId} not found` + (appendedMessage ? `: ${appendedMessage}` : ""), options);
        this.entityName = entityName;
        this.entityId = entityId;
    }
}
class EntitiesNotFoundError extends BaseError {
    entityName;
    entityIds;
    #tag = "EntitiesNotFoundError";
    name = "EntitiesNotFoundError";
    statusCode = 404;
    constructor(entityName, entityIds, appendedMessage, options) {
        super(`${entityName} ${entityIds} not found` + (appendedMessage ? `: ${appendedMessage}` : ""), options);
        this.entityName = entityName;
        this.entityIds = entityIds;
    }
}
class EntityAlreadyExistsError extends BaseError {
    entityName;
    entityId;
    appendedMessage;
    #tag = "EntityAlreadyExistsError";
    name = "EntityAlreadyExistsError";
    statusCode = 409;
    constructor(entityName, entityId, appendedMessage, options) {
        super(`${entityName} ${entityId} already exists` + (appendedMessage ? `: ${appendedMessage}` : ""), options);
        this.entityName = entityName;
        this.entityId = entityId;
        this.appendedMessage = appendedMessage;
    }
}
class EntitySubTypeError extends BaseError {
    entityName;
    entityId;
    subType;
    #tag = "EntitySubTypeError";
    name = "EntitySubTypeError";
    constructor(entityName, entityId, subType, options) {
        super(`Invalid sub type ${subType} for ${entityName} with id ${entityId}`, options);
        this.entityName = entityName;
        this.entityId = entityId;
        this.subType = subType;
    }
}
class CreateEntityError extends BaseError {
    entityName;
    createPayload;
    parseError;
    #tag = "CreateEntityError";
    name = "CreateEntityError";
    statusCode = 400;
    constructor(entityName, createPayload, parseError, options) {
        super(`Unable to create ${entityName} ${JSON.stringify(createPayload, null, 2)} with error ${JSON.stringify(parseError, null, 2)}`, options);
        this.entityName = entityName;
        this.createPayload = createPayload;
        this.parseError = parseError;
    }
}
class ParseEntityError extends BaseError {
    entityName;
    parsePayload;
    parseError;
    #tag = "ParseEntityError";
    name = "ParseEntityError";
    statusCode = 500;
    constructor(entityName, parsePayload, parseError, options) {
        super(`Unable to parse ${entityName} ${JSON.stringify(parsePayload, null, 2)} with error ${JSON.stringify(parseError, null, 2)}`, options);
        this.entityName = entityName;
        this.parsePayload = parsePayload;
        this.parseError = parseError;
    }
}
class UpdateEntityError extends BaseError {
    entityName;
    id;
    updatePayload;
    parseError;
    #tag = "UpdateEntityError";
    name = "UpdateEntityError";
    statusCode = 400;
    constructor(entityName, id, updatePayload, parseError, options) {
        super(`Unable to update ${entityName} ${id} with values ${JSON.stringify(updatePayload, null, 2)} with error ${JSON.stringify(parseError, null, 2)}`, options);
        this.entityName = entityName;
        this.id = id;
        this.updatePayload = updatePayload;
        this.parseError = parseError;
    }
}
class UpdateEntitiesError extends BaseError {
    entityName;
    ids;
    updatePayload;
    parseError;
    #tag = "UpdateEntitiesError";
    name = "UpdateEntitiesError";
    statusCode = 400;
    constructor(entityName, ids, updatePayload, parseError, options) {
        super(`Unable to update ${entityName} ${ids} with values ${JSON.stringify(updatePayload, null, 2)} with error ${JSON.stringify(parseError, null, 2)}`, options);
        this.entityName = entityName;
        this.ids = ids;
        this.updatePayload = updatePayload;
        this.parseError = parseError;
    }
}
class UpsertEntityError extends BaseError {
    entityName;
    upsertPayload;
    parseError;
    #tag = "UpsertEntityError";
    name = "UpsertEntityError";
    statusCode = 400;
    constructor(entityName, upsertPayload, parseError, options) {
        super(`Unable to upsert ${entityName} with values ${JSON.stringify(upsertPayload, null, 2)} with error ${JSON.stringify(parseError, null, 2)}`, options);
        this.entityName = entityName;
        this.upsertPayload = upsertPayload;
        this.parseError = parseError;
    }
}
class UpsertEntitiesError extends BaseError {
    entityName;
    ids;
    upsertPayload;
    parseError;
    #tag = "UpsertEntitiesError";
    name = "UpsertEntitiesError";
    statusCode = 400;
    constructor(entityName, ids, upsertPayload, parseError, options) {
        super(`Unable to upsert ${entityName} ${ids} with values ${JSON.stringify(upsertPayload, null, 2)} with error ${JSON.stringify(parseError, null, 2)}`, options);
        this.entityName = entityName;
        this.ids = ids;
        this.upsertPayload = upsertPayload;
        this.parseError = parseError;
    }
}
class DeleteEntityError extends BaseError {
    entityName;
    id;
    #tag = "DeleteEntityError";
    name = "DeleteEntityError";
    statusCode = 400;
    constructor(entityName, id, options) {
        super(`Unable to delete ${entityName} ${id}`, options);
        this.entityName = entityName;
        this.id = id;
    }
}
class EncryptionError extends BaseError {
    #tag = "EncryptionError";
    name = "EncryptionError";
    statusCode = 500;
    constructor(message, options) {
        super(message, options);
    }
}
class VendorError extends BaseError {
    vendorName;
    #tag = "VendorError";
    name = "VendorError";
    constructor(vendorName, message, options) {
        super(message, options);
        this.vendorName = vendorName;
    }
}
class ResponseDtoParseError extends BaseError {
    dtoName;
    context;
    #tag = "ResponseDtoParseError";
    name = "ResponseDtoParseError";
    statusCode = 500;
    issues;
    constructor(dtoName, context, parseError, options) {
        super("Unable to parse response DTO", options);
        this.dtoName = dtoName;
        this.context = context;
        this.issues = parseError.issues.map(({ path, code, message }) => ({
            path,
            code,
            message,
        }));
    }
}
class BadGatewayError extends BaseError {
    message;
    #tag = "BadGatewayError";
    name = "BadGatewayError";
    statusCode = 502;
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class UnprocessableContentError extends BaseError {
    message;
    #tag = "UnprocessableContentError";
    name = "UnprocessableContentError";
    statusCode = 422;
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class PDFError extends BaseError {
    message;
    #tag = "PDFError";
    name = "PDFError";
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class ImageError extends BaseError {
    message;
    #tag = "ImageError";
    name = "ImageError";
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class FeatureDisabledError extends BaseError {
    message;
    #tag = "FeatureDisabledError";
    name = "FeatureDisabledError";
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class ConfigError extends BaseError {
    configPath;
    #tag = "ConfigError";
    name = "ConfigError";
    constructor(configPath, options) {
        super("Invalid config path: " + configPath, options);
        this.configPath = configPath;
    }
}
class HttpClientError extends BaseError {
    message;
    statusCode;
    #tag = "HttpClientError";
    name = "HttpClientError";
    detail;
    constructor(message, statusCode = 500, options) {
        super(message, options);
        this.message = message;
        this.statusCode = statusCode;
        this.detail = options?.detail;
    }
}
class TooManyRequestsError extends BaseError {
    message;
    #tag = "TooManyRequestsError";
    name = "TooManyRequestsError";
    statusCode = 429;
    constructor(message, options) {
        super(message, options);
        this.message = message;
    }
}
class FileIsEncryptedError extends BaseError {
    message;
    options;
    #tag = "FileIsEncryptedError";
    name = "FileIsEncryptedError";
    statusCode = 422;
    key = "file_is_encrypted";
    constructor(message, options) {
        super(message, options);
        this.message = message;
        this.options = options;
    }
}
class DocumentProcessingError extends BaseError {
    #tag = "DocumentProcessingError";
    constructor(message, options) {
        super(`DocumentProcessingError: ${message}`, options);
    }
}
class OCRProcessingError extends DocumentProcessingError {
    #tag = "OCRProcessingError";
    constructor(message, options) {
        super(`OCR Processing failed: ${message}`, options);
    }
}
class PreprocessingError extends DocumentProcessingError {
    #tag = "PreprocessingError";
    constructor(message, options) {
        super(`Preprocessing failed: ${message}`, options);
    }
}
class AIProcessingError extends BaseError {
    #tag = "AIProcessingError";
    statusCode;
    constructor(message, statusCode, options) {
        super(`AI Processing failed: ${message}`, options);
        this.statusCode = statusCode;
    }
}
class DocumentNotFound extends BaseError {
    #tag = "DocumentNotFoundError";
    constructor(message, options) {
        super(`DocumentNotFound Error: ${message}`, options);
    }
}
class NumberParseError extends BaseError {
    message;
    #tag = "NumberParseError";
    name = "NumberParseError";
    statusCode = 400;
    constructor(message) {
        super(message);
        this.message = message;
    }
}

export { AIProcessingError, BadGatewayError, BadRequestError, BaseError, BondingLinePlacementError, ConfigError, ConflictError, CreateEntityError, DatabaseError, DeleteEntityError, DocumentNotFound, DocumentProcessingError, EncryptionError, EntitiesNotFoundError, EntityAlreadyExistsError, EntityNotFoundError, EntitySubTypeError, FeatureDisabledError, FileIsEncryptedError, HttpClientError, ImageError, InternalServerError, NumberParseError, OCRProcessingError, PDFError, ParseEntityError, PreprocessingError, ResponseDtoParseError, StaleVersionError, TooManyRequestsError, UnprocessableContentError, UpdateEntitiesError, UpdateEntityError, UpsertEntitiesError, UpsertEntityError, VendorError };
